/**
 * Echo Hunt -- live (DB-backed) wiring for the judge loop (P10).
 *
 * Per the ground rules (§16.0), the loop itself lives in the pure `Judge.ts`; this wrapper is where the
 * things it deliberately does not know about are supplied:
 *
 *  1. **The settings** (`echoProfiles.judge`), with §16.1's defaults for a profile created before P10.
 *  2. **The resample callbacks** (§9.3). Resampling a fake means one fresh generator call; resampling a
 *     real means drawing another corpus bullet and re-running the P7 reshape on it. Both need the corpus,
 *     the word list and the profile, none of which a pure module may touch.
 *  3. **The retention gate** for a repair rewrite, reused from `Reshape.ts` (`CheckRetention`) so a repair
 *     is held to exactly the same standard as a reshape -- one rule, one place.
 *  4. **Persistence** (`JudgeRound_Live`): the repaired texts, `judgeLog`, `status` and `judgeSummary`
 *     are written back onto the round.
 *
 * ## When the loop runs
 *
 * `GenerateRound_Live` calls it right after a validated weave, which is the order §3 specifies (`…weave →
 * JUDGE LOOP → HUNT`): a round the user is about to play must already be stranger-proof, or its score is
 * measuring the generator rather than the user (§2 invariant #1).
 *
 * Judging an EXISTING round is also possible (`JudgeRound_Live` takes any round), which is what makes
 * rounds generated before P10 judgeable instead of stuck as `unjudged` forever.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §9, §3, §16.2 P10.
 */
import {GetAsync} from "mobx-graphlink";
import {UpdateEchoRound} from "../Server/Commands/EchoRoundCommands.js";
import {EchoRound} from "../Server/DB/echoRounds/@EchoRound.js";
import {GetEchoStories} from "../Server/DB/echoStories/@EchoStory.js";
import {MeID} from "../Server/DB/users.js";
import {EchoCorpusBullet} from "../Server/DB/echoCorpusBullets/@EchoCorpusBullet.js";
import {LLMCallCounter} from "./LLM.js";
import {liveLLMBackend} from "./LLM_Live.js";
import {CheckRetention} from "./Reshape.js";
import {
	DefaultJudgeLoopSettings,
	JudgeLoopResult,
	JudgeLoopSettings,
	JudgeLoopSlot,
	RunJudgeLoop,
	RunSingleRepair,
	ResolveSlotSandboxText,
	NextRepairAttemptIndex,
} from "./Judge.js";
import {GetCheckOptionsFromProfile} from "./Bullets_Live.js";
import {GetJudgeSettings, GetMyEchoProfile} from "./Profiles_Live.js";
import {
	GetBulletFamilyId,
	GetRealBulletsForPool,
	RowToRealBulletForPool,
} from "./Reshape_Live.js";
import {GetUsableCorpusRows} from "./FakeGenerator_Live.js";
import {GetSeedWordlist} from "./Wordlist_Live.js";
import {GenerateFake} from "./FakeGenerator.js";
import {GetConceptCountRange, GetFakeDistributions} from "./FakeGenerator_Live.js";
import {ReshapeBulletWithRetention, SampleDistinctWords, SampleReshapeWordCount} from "./Reshape.js";
import {CreateCharacter, PickRandomWorld} from "./Worlds.js";
import {GetWorldPool_Live} from "./Weave_Live.js";
import {ResolveStoryCharacter_Live} from "./Characters_Live.js";
import {IsRoundUnjudged} from "./Round.js";
import {EchoCharacter, EchoWorld, GeneratedBullet, JudgeRepair, RoundSlot, SlotRepairAttempt} from "./Types.js";

// settings (§9.1, §9.2, §9.3)
// ==========

/** Builds the pure loop's settings from the profile: the stored knobs, plus the per-role model overrides
 * resolved into the loop's judge list.
 *
 * SYNC, and it reads the profile, so call this from a path that already wrapped `GetAsync` (or from a
 * React `observer`). `GetJudgeSettings` itself lives in `Profiles_Live.ts` -- it only needs the profile
 * row, and putting it here would make `Reshape_Live` (which reads it for the rejection-sampling toggle)
 * import this module, which imports `Reshape_Live` back.
 *
 * `loopJudgeCalls` is derived rather than stored: §9.2 asks for "2 judge calls or 2 model families", so
 * two is the floor and a longer model list buys one call per model. That keeps "add another judge model"
 * meaningful without a second setting to keep in sync.
 */
export function GetJudgeLoopSettings(): JudgeLoopSettings {
	const profile = GetMyEchoProfile();
	const settings = GetJudgeSettings();
	const judgeModels = (settings.judgeModels ?? []).map(a=>a.trim()).filter(a=>a.length > 0);

	return {
		bandMin: settings.bandMin,
		bandMax: settings.bandMax,
		// a stored list wins; otherwise the `judge` role's single model is called twice (§9.2's cheap form)
		judgeModels: judgeModels.length > 0 ? judgeModels : [profile?.modelRoles?.judge ?? ""].filter(a=>a.length > 0),
		loopJudgeCalls: Math.max(2, judgeModels.length),
		maxAttempts: Math.max(1, settings.maxAttempts ?? DefaultJudgeLoopSettings().maxAttempts),
		rewriteBudgetPerSlot: Math.max(0, settings.rewriteBudgetPerSlot ?? DefaultJudgeLoopSettings().rewriteBudgetPerSlot),
		heldOutJudgeModel: profile?.modelRoles?.heldOutJudge ?? null,
	};
}

// the loop
// ==========

export type JudgeRoundResult =
	| {ok: true, result: JudgeLoopResult, round: EchoRound}
	| {ok: false, error: string};

/** The corpus rows a round's real slots came from, keyed by corpus row id -- what the retention gate needs
 * to compare a repair rewrite against the ORIGINAL event (§9.3). */
function GetCorpusRowsByID(): Map<string, EchoCorpusBullet> {
	const rows = new Map<string, EchoCorpusBullet>();
	for (const row of GetUsableCorpusRows()) rows.set(row.id, row);
	return rows;
}

/** Attaches each real slot's indexed source event to it, so a repair rewrite can be retention-gated
 * (`sourceEventText`). A missing corpus row leaves it undefined, and `Judge.ts` then falls back to the
 * slot's own reshaped text -- which is the best available definition of "the event" anyway. */
function ToJudgeLoopSlots(slots: RoundSlot[], corpus: Map<string, EchoCorpusBullet>): JudgeLoopSlot[] {
	return slots.map(slot=>({
		...slot,
		sourceEventText: slot.sourceKind == "real" ? (corpus.get(slot.sourceId)?.selfContainedText ?? null) : null,
	}));
}

/** Resamples a REAL slot (§9.3): draws a different corpus bullet, reshapes it with the shared reshape, and
 * returns both the new text and the corpus row it came from.
 *
 * Unused-first, not random: a round that already contains a bullet from a dream family must not get a
 * SECOND bullet from the same night (§4.5's one-per-family rule), and `slots` is the only place that
 * information exists at this point. Returns null when no unused real bullet is available.
 */
async function ResampleReal_Live(slots: RoundSlot[], counter?: LLMCallCounter|n): Promise<{text: string, sourceId: string, outputTokens: number}|null> {
	const profile = GetMyEchoProfile();
	const wordlist = GetSeedWordlist(profile);
	if (wordlist == null || wordlist.words.length == 0) return null;

	const usedSourceIds = new Set(slots.map(a=>a.sourceId));
	const usedFamilies = new Set(slots.filter(a=>a.sourceKind == "real").map(a=>GetBulletFamilyId(a.sourceId)));
	const candidates = GetRealBulletsForPool();
	const replacement = candidates.find(a=>!usedSourceIds.has(a.id) && !usedFamilies.has(a.familyId))
		?? candidates.find(a=>!usedSourceIds.has(a.id))
		?? null;
	if (replacement == null) return null;

	const wordCount = SampleReshapeWordCount(replacement.text, Math.random);
	const seedWords = wordCount == 0 ? [] : SampleDistinctWords(wordlist.words, wordCount, Math.random);
	if (seedWords.length == 0) return null;

	const reshaped = await ReshapeBulletWithRetention(liveLLMBackend, replacement.text, {
		isReal: true,
		sourceText: replacement.text,
		seedWords,
		leakReferences: replacement.leakReferences,
		bannedWords: profile?.bannedTerms ?? [],
		blacklist: (profile?.clicheBlacklist ?? []).map(a=>a.phrase),
		model: profile?.modelRoles?.reshape ?? null,
		counter,
	});
	if (reshaped.text == null) return null;

	return {text: reshaped.text, sourceId: replacement.id, outputTokens: reshaped.outputTokens};
}

/** Resamples a FAKE slot (§9.3): one fresh, independent generator call -- exactly as §5.1 requires, so the
 * replacement cannot be "written for" the story it is joining. Returns the whole result (not just the
 * bullet) so the caller can log the rejected candidates too. */
async function ResampleFake_Live(counter?: LLMCallCounter|n): Promise<{bullet: GeneratedBullet, outputTokens: number}|null> {
	const profile = GetMyEchoProfile();
	const wordlist = GetSeedWordlist(profile);
	if (wordlist == null || wordlist.words.length == 0) return null;

	const generated = await GenerateFake(liveLLMBackend, {
		wordlist: wordlist.words,
		distributions: GetFakeDistributions(),
		conceptCountRange: GetConceptCountRange(),
		rng: Math.random,
		blacklist: (profile?.clicheBlacklist ?? []).map(a=>a.phrase),
		bannedWords: profile?.bannedTerms ?? [],
		model: profile?.modelRoles?.generator ?? null,
		counter,
	});
	if (generated.bullet == null) return null;
	return {bullet: generated.bullet, outputTokens: generated.outputTokens};
}

/** Runs the judge loop over a round's woven slots and PERSISTS the outcome (§9.1-§9.5).
 *
 * Persists four things:
 *  - the slots, because a repair changes `finalText`/`rewriteCount`/`resampleCount` and the player must
 *    read the repaired text (that is the whole point of repairing it);
 *  - `judgeLog`, every call in order, for §13.1;
 *  - `status`, per §9.4 (`ok` when it landed in the band, `easy` otherwise);
 *  - `judgeSummary`, which is what the reveal reports and what P13's stats read (§13.3 personal margin).
 *
 * A judged round is still playable when it lands `easy`: §9.4 allows either discarding or showing it
 * flagged. This wrapper always keeps it and flags it, because a discarded round is a round the user paid
 * for and cannot see -- the reveal says plainly that a stranger could have told.
 */
export async function JudgeRound_Live(round: EchoRound, options?: {counter?: LLMCallCounter|n, skipHeldOut?: boolean}): Promise<JudgeRoundResult> {
	const slots = round.slots ?? [];
	if (slots.length == 0) return {ok: false, error: "That round has no woven bullets to judge."};

	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return {ok: false, error: "No Echo Hunt profile -- one is needed to read the judge settings."};

	// the world/character come from the round itself where possible, so re-judging an old round uses the
	// setting it was generated in rather than whatever the story has moved on to
	const world = ResolveRoundWorld(round);
	const character = ResolveRoundCharacter(round);
	const counter = options?.counter ?? new LLMCallCounter();

	// where a resampled slot's replacement came from. Kept local to this call rather than module-level, so
	// two rounds judged in one session cannot cross-contaminate. A resampled real slot now points at a
	// DIFFERENT corpus row, so the round's provenance has to follow it -- otherwise the reveal would show
	// the old dream's text and the exposure counters would credit a bullet the user never saw.
	const replacementSourceIdBySlot = new Map<string, string>();
	const replacementFakes: GeneratedBullet[] = [];

	const result = await RunJudgeLoop(liveLLMBackend, {
		slots: ToJudgeLoopSlots(slots, GetCorpusRowsByID()),
		settings: GetJudgeLoopSettings(),
		world,
		character,
		summary: round.storylineSummaryIn ?? null,
		xShare: round.xShare ?? profile.xShare ?? .65,
		counter,
		checkOptions: GetCheckOptionsFromProfile(),
		resampleSlot: async(slot, repair)=>{
			if (repair == "resampleFake") {
				const replacement = await ResampleFake_Live(counter);
				if (replacement == null) return null;
				replacementFakes.push(replacement.bullet);
				return {text: replacement.bullet.text, outputTokens: replacement.outputTokens};
			}
			if (repair == "resampleReal") {
				const replacement = await ResampleReal_Live(slots, counter);
				if (replacement == null) return null;
				// the loop records this on the slot's repair history; the caller patches `sourceId` from it below
				replacementSourceIdBySlot.set(slot.slotId, replacement.sourceId);
				return {text: replacement.text, newSourceId: replacement.sourceId, outputTokens: replacement.outputTokens};
			}
			return null;
		},
		// the SAME retention rule the reshape uses, so a repair is held to one standard rather than two
		checkRetention: async(sourceText, rewrittenText)=>{
			const retention = await CheckRetention(liveLLMBackend, sourceText, rewrittenText, {model: profile.modelRoles?.retention ?? null, counter});
			return retention.same;
		},
		skipHeldOut: options?.skipHeldOut,
	});

	// a resampled real slot now points at a DIFFERENT corpus row, so the round's provenance has to follow
	// it -- otherwise the reveal would show the old dream's text and the exposure counters would credit a
	// bullet the user never saw
	const finalSlots = result.slots.map(slot=>({
		...slot,
		sourceId: replacementSourceIdBySlot.get(slot.slotId) ?? slot.sourceId,
	}));

	const generatedBullets = [...(round.generatedBullets ?? []), ...replacementFakes];

	await new UpdateEchoRound({id: round.id, updates: {
		slots: finalSlots,
		judgeLog: [...(round.judgeLog ?? []), ...result.judgeLog],
		judgeSummary: result.summary,
		status: result.status,
		generatedBullets,
		outputTokensTotal: (round.outputTokensTotal ?? 0) + counter.outputTokens,
		llmCallsTotal: (round.llmCallsTotal ?? 0) + counter.callCount,
	}}).RunOnServer();

	return {
		ok: true,
		result,
		round: new EchoRound({...round, slots: finalSlots, judgeSummary: result.summary, status: result.status}),
	};
}

/** The world a round was generated in (§8.5). Falls back to a stable placeholder when the round predates
 * P8 or the world has since been deleted from the pool: a judge with no world bible is still a perfectly
 * good judge, and refusing to judge the round would leave it permanently `unjudged`. */
export function ResolveRoundWorld(round: EchoRound): EchoWorld {
	const pool = GetWorldPool_Live();
	const world = pool.find(a=>a.id == round.worldId);
	if (world != null) return world;
	return PLACEHOLDER_WORLD;
}

/** The stand-in used when a round's world is no longer in the pool. Exported so the inspector can say
 * "this world is gone" rather than silently showing a fake bible as if it were the real one. */
export const PLACEHOLDER_WORLD: EchoWorld = {
	id: "world_unknown",
	name: "(world no longer in the pool)",
	bible: "An unremarkable place. Use only what the installment itself says.",
	crazinessTolerance: .9,
	generated: false,
};

/** The character a round was generated with (§8.2).
 *
 * Read through the round's STORY: the story holds the character's ID (§8.7), and the entry is looked up
 * in the pool. That is deliberate -- editing a character is meant to change the running story, so a
 * re-judge or a later repair sees the current version of the person rather than a copy taken at round
 * time.
 *
 * Only a round with no story, or a story whose character was deleted, falls back to a generated
 * character: a rewrite uses it for tone alone, so it cannot corrupt a bullet's event, and refusing to
 * re-judge would leave the round permanently unjudged. `RoundCharacterIsPlaceholder` reports exactly
 * that case so the inspector can say so. */
export function ResolveRoundCharacter(round: EchoRound): EchoCharacter {
	const userID = MeID();
	if (round.storyId != null && userID != null) {
		const story = GetEchoStories(userID).find(a=>a.id == round.storyId) ?? null;
		const character = ResolveStoryCharacter_Live(story);
		if (character != null) return character;
	}
	return CreateCharacter(Math.random);
}

/** True when a round's character had to be invented (no story, or the story's character was deleted), so
 * the inspector can mark it. */
export function RoundCharacterIsPlaceholder(round: EchoRound): boolean {
	const userID = MeID();
	if (round.storyId == null || userID == null) return true;
	const story = GetEchoStories(userID).find(a=>a.id == round.storyId) ?? null;
	return ResolveStoryCharacter_Live(story) == null;
}

/** True when a round has never been through the judge loop, i.e. it still carries the P9 `unjudged` status.
 * The Play tab offers a "judge this round" action for these, so rounds generated before P10 are not stuck.
 * Delegates to the pure module's definition so the UI and the loop cannot disagree about it. */
export function NeedsJudging(round: EchoRound|null|n): boolean {
	return IsRoundUnjudged(round);
}

// manual per-slot retry (P10 debug view)
// ==========

export type RepairSlotResult =
	| {
		ok: true;
		result: JudgeLoopResult;
		round: EchoRound;
		history: SlotRepairAttempt;
		/** the slot's text AFTER this retry, i.e. the played text with the manual attempts replayed. Deliberately
		 * separate from `Slots`' own `finalText`, which still records what the player actually read. */
		sandboxText: string;
	}
	| {ok: false, error: string};

/** Re-runs ONE user-chosen repair on ONE slot, then re-judges the story once (§9.3). This is the debug
 * view's "retry just this entry, with this remedy" action.
 *
 * Two design points:
 *  1. **The repair is FORCED, not chosen.** The loop's own heuristic picked something already and it did not
 *     work; letting the user say "try a resample instead" is the experiment that tells them why.
 *  2. **The history is APPENDED, not replaced.** `attemptIndexOffset` continues the numbering past the
 *     existing attempts, so the user sees their manual retry after the automatic ones instead of losing the
 *     record of what was already tried.
 *
 * The round's `judgeSummary` is deliberately NOT rewritten from this single re-judge: the summary describes
 * the round the player was given, and one slot's manual experiment would otherwise make it claim a
 * difficulty the stored story never had. The new attempt's AUC is returned on `result` for display instead.
 */
export async function RetrySlotRepair_Live(round: EchoRound, slotId: string, repair: JudgeRepair): Promise<RepairSlotResult> {
	const slots = round.slots ?? [];
	if (slots.length == 0) return {ok: false, error: "That round has no woven bullets."};
	if (!slots.some(slot=>slot.slotId == slotId)) return {ok: false, error: "That bullet is not in this round."};

	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return {ok: false, error: "No Echo Hunt profile -- one is needed to read the judge settings."};

	const counter = new LLMCallCounter();
	const replacementFakes: GeneratedBullet[] = [];

	const result = await RunSingleRepair(liveLLMBackend, repair, slotId, {
		slots: ToJudgeLoopSlots(slots, GetCorpusRowsByID()),
		settings: GetJudgeLoopSettings(),
		world: ResolveRoundWorld(round),
		character: ResolveRoundCharacter(round),
		summary: round.storylineSummaryIn ?? null,
		xShare: round.xShare ?? profile.xShare ?? .65,
		counter,
		checkOptions: GetCheckOptionsFromProfile(),
		// the SAME callbacks the automatic loop uses, so a manual retry exercises the real code path
		resampleSlot: async(slot, kind)=>{
			if (kind == "resampleFake") {
				const replacement = await ResampleFake_Live(counter);
				if (replacement == null) return null;
				replacementFakes.push(replacement.bullet);
				return {text: replacement.bullet.text, outputTokens: replacement.outputTokens};
			}
			if (kind == "resampleReal") {
				const replacement = await ResampleReal_Live(slots, counter);
				if (replacement == null) return null;
				return {text: replacement.text, newSourceId: replacement.sourceId, outputTokens: replacement.outputTokens};
			}
			return null;
		},
		checkRetention: async(sourceText, rewrittenText)=>{
			const retention = await CheckRetention(liveLLMBackend, sourceText, rewrittenText, {model: profile.modelRoles?.retention ?? null, counter});
			return retention.same;
		},
		// number this attempt after everything already on record (loop attempts AND earlier manual retries), so
		// two retries in a row are distinguishable in the panel
		attemptIndexOffset: NextRepairAttemptIndex(slots),
		// the held-out judge has nothing useful to say about a one-slot experiment, and this is a debug action
		// the user may press repeatedly
		skipHeldOut: true,
	});

	// NO `sourceId` patching here: a manual retry must not rewrite the round's provenance, or the reveal would
	// show a different dream's raw text than the bullet the player actually saw. A resampled real's new row is
	// recorded on its history entry (`newSourceId`) for the inspector to display instead.
	const finalSlots = result.slots;

	const generatedBullets = [...(round.generatedBullets ?? []), ...replacementFakes];
	const targetSlot = finalSlots.find(slot=>slot.slotId == slotId)!;
	const history = (targetSlot.repairHistory ?? []).slice(-1)[0]
		?? {attemptIndex: 0, repair, source: "manual" as const, textBefore: "", textAfter: null, applied: false, reason: "the repair was not recorded", createdAt: Date.now()};
	const sandboxText = ResolveSlotSandboxText(targetSlot);

	await new UpdateEchoRound({id: round.id, updates: {
		slots: finalSlots,
		generatedBullets,
		outputTokensTotal: (round.outputTokensTotal ?? 0) + counter.outputTokens,
		llmCallsTotal: (round.llmCallsTotal ?? 0) + counter.callCount,
	}}).RunOnServer();

	return {
		ok: true,
		result,
		round: new EchoRound({...round, slots: finalSlots, generatedBullets}),
		history,
		sandboxText,
	};
}
